import { useCallback, useMemo, useRef } from 'react';

import { type IntentConfiguration } from '@stripe/stripe-react-native';
import { useActorRef, useSelector } from '@xstate5/react';
import * as DateFns from 'date-fns';
import { ActorRef } from 'xstate5';

import {
  PaymentStatePollingActors,
  usePaymentStatePollingMachine,
} from '@sg/shared-fe/features/ordering';

import { useAwsWafTokenRef } from '@order/integrations/aws-waf';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { sumLedgerTotal } from '../components';
import {
  useBagLoyaltyRewardsDeferred,
  useOrderPaymentStateErrorHandler,
  useShowOrderPaymentStateErrorOnVisible,
} from '../hooks';
import { adjustWantedTime, clearLocalStorageOrderId, getOrderPaymentStateConfig } from '../utils';
import { orderingMachine } from './ordering-machine';
import { type OrderingMachineAction } from './ordering-machine.actions.types';
import { type OrderingActors } from './ordering-machine.actors';
import { type WantedTime } from './ordering-machine.types';
import { getMaxAvailableUpsellItems } from './ordering-machine.utils';

import type { BagReward, OrderingContext } from './ordering-machine.context';
import type {
  AddUpsellToBag,
  ChangeDeliveryPreferences,
  ChangeLineItemQuantity,
  ChangeUtensilsUsage,
  OrderingEvent,
} from './ordering-machine.events';

// ─────────────────────────────────────────────────────────────────────────────

export const useOrderingMachine = (params: UseOrderingParams) => {
  const {
    isLoggedIn,
    isPaymentStatePollingEnabled,
    orderSubmissionDelay,
    bagCartPollingInterval,
    bagTracking,
    handlePostOrderPurchaseModal,
    handlePostOrderNotifications,
    handlePostOrderTracking,
    handlePostOrderHistory,
    handlePostOrderAppReview,
    navigatePostOrder,
    changeChannelOrLocation,
    requestLogin,
    fetchInitialData,
    fetchOrderPaymentState,
    fetchStatePostOrder,
    fetchRewards,
    fetchWantedTimes,
    fetchBagCartPolling,
    addUpsellToBag,
    updateLineItem,
    applyReward,
    removeReward,
    applyPromoCode,
    applyGiftCard,
    cancelPreviousIntent,
    submitOrder,
  } = params;

  // ─── Tracking ────────────────────────────────────────────────────────────────

  const {
    trackBagView,
    trackLocationNamePressed,
    trackWantedTimePressed,
    trackWantedTimeChanged,
    trackDeliveryPreferencesPressed,
    trackDropoffLocationChanged,
    trackLineItemQuantityChanged,
    trackLineItemRemoved,
    trackUtensilsUsagePressed,
    trackLoyaltyInfoPressed,
    trackLoyaltyTermsPressed,
    trackApplyRewardPressed,
    trackRemoveRewardPressed,
    trackPromoCodeAddPressed,
    trackChangePaymentMethodPressed,
    trackCreditPressed,
    trackRedeemGiftCardPressed,
    trackRedeemGiftCardSubmitted,
    trackUpsellExperimentStarted,
  } = bagTracking;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripeConfirmHandlerRef = useRef<NonNullable<IntentConfiguration['confirmHandler']>>(
    () => {},
  );

  const registerStripeConfirmHandler = useCallback(
    (handler: NonNullable<IntentConfiguration['confirmHandler']>) => {
      stripeConfirmHandlerRef.current = handler;
    },
    [],
  );

  // ─── Payment State Polling ─────────────────────────────────────────────────

  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config');
  const maxNumberOfUpsellProducts = useFeatureFlag('permanent-order-upsell-number-of-products');

  const { handleOnVisibleRef, onOrderPaymentStateError } = useOrderPaymentStateErrorHandler(); // prettier-ignore

  const {
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
  } = getOrderPaymentStateConfig(orderPaymentStateConfig);

  const actorRefRef = useRef<ActorRef<any, OrderingEvent>>(null);

  const incrementPurchaseAttempt = useCallback(() => {
    actorRefRef.current?.send({ type: 'INCREMENT_PURCHASE_ATTEMPT' });
  }, []);

  const onPaymentFulfilled = useCallback(() => {
    actorRefRef.current?.send({ type: 'PAYMENT_FULFILLED' });
  }, []);

  const onPaymentCanceled = useCallback(() => {
    actorRefRef.current?.send({ type: 'PAYMENT_CANCELED' });
  }, []);

  const handlePaymentError = useCallback(
    (params: Parameters<typeof onOrderPaymentStateError>[0]) => {
      actorRefRef.current?.send({ type: 'PAYMENT_CANCELED' });
      onOrderPaymentStateError(params);
    },
    [onOrderPaymentStateError],
  );

  const {
    paymentState,
    startPolling,
    createPayment,
    clearPaymentState,
    failPayment,
    requestPaymentCancelation,
    isPollingPayment,
    isPaymentPending,
    isPaymentDelayed,
    isPaymentFulfilled,
    isCancelingPayment,
  } = usePaymentStatePollingMachine({
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
    fetchPaymentState: fetchOrderPaymentState,
    cancelPayment: cancelPreviousIntent,
    clearPendingPayment: clearLocalStorageOrderId,
    incrementPurchaseAttempt,
    onPaymentStateError: handlePaymentError,
    onPaymentFulfilled,
    onPaymentCanceled,
  });

  const startPollingPaymentState = useCallback(
    (params: { context: OrderingContext }) => {
      const { context } = params;
      startPolling(context.orderId ?? context.cart?.orderId ?? '');
    },
    [startPolling],
  );

  // ─── Guards ────────────────────────────────────────────────────────────────

  const checkIfLoggedIn = useCallback(() => {
    return isLoggedIn;
  }, [isLoggedIn]);

  const checkIfShouldFetchInitialPaymentState = useCallback(() => {
    return isLoggedIn && isPaymentStatePollingEnabled;
  }, [isLoggedIn, isPaymentStatePollingEnabled]);

  const checkIfBagCartPollingEnabled = useCallback(() => {
    return bagCartPollingInterval > 0;
  }, [bagCartPollingInterval]);

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const wantedTimesPollingInterval = useFeatureFlag('permanent-bag-wanted-times-polling-interval-milliseconds'); // prettier-ignore
  const placementVariation = useFeatureFlag('cels-5394-order-upsell-placement-experiment');

  const actorRef = useActorRef(
    orderingMachine.provide({
      actions: {
        handlePostOrderPurchaseModal,
        handlePostOrderNotifications,
        handlePostOrderTracking,
        handlePostOrderHistory,
        handlePostOrderAppReview,
        navigatePostOrder,
        changeChannelOrLocation,
        requestLogin,
        clearLocalStorageOrderId,
        startPollingPaymentState,
        trackUpsellExperimentStarted: ({ context }) => {
          trackUpsellExperimentStarted(context.placementVariation);
        },
      },
      actors: {
        fetchInitialData,
        fetchStatePostOrder,
        fetchRewards,
        fetchWantedTimes,
        fetchBagCartPolling,
        addUpsellToBag,
        updateLineItem,
        applyReward,
        removeReward,
        applyPromoCode,
        applyGiftCard,
        submitOrder,
      },
      guards: {
        checkIfLoggedIn,
        checkIfShouldFetchInitialPaymentState,
        checkIfBagCartPollingEnabled,
      },
    }),
    {
      input: {
        orderSubmissionDelay,
        wantedTimesPollingInterval,
        bagCartPollingInterval,
        isPaymentStatePollingEnabled,
        stripeConfirmHandlerRef,
        maxNumberOfUpsellProducts,
        placementVariation,
      },
    },
  );

  actorRefRef.current = actorRef;
  const context = useSelector(actorRef, (snapshot) => snapshot.context);
  useShowOrderPaymentStateErrorOnVisible(paymentState, handleOnVisibleRef);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingInitialData = useSelector(actorRef, (state) => {
    return (
      state.matches({ ordering: { loading: 'fetchingInitialData' } }) ||
      state.matches({ ordering: { loading: 'retrievingInitialWantedTime' } }) ||
      state.matches({ rewards: 'preparingToFetchRewards' }) ||
      state.matches({ rewards: 'fetchingRewardsDeferred' })
    );
  });

  const isFetchingRewards = useSelector(actorRef, (state) => {
    return (
      state.matches({ rewards: 'preparingToFetchRewards' }) ||
      state.matches({ rewards: 'fetchingRewards' }) ||
      state.matches({ rewards: 'fetchingRewardsDeferred' })
    );
  });

  const isFetchingRewardsDeferred = useSelector(actorRef, (state) => {
    return state.matches({ rewards: 'fetchingRewardsDeferred' });
  });

  const hasFailedToLoadRewards = useSelector(actorRef, (state) => {
    return state.matches({ rewards: 'error' });
  });

  const isChangingLineItemQuantity = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'changingLineItemQuantity' },
    });
  });

  const isApplyingPromoCode = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'applyingPromoCode' },
    });
  });

  const isChangingTime = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'changingTime' } });
  });

  const isChangingDeliveryPreferences = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'changingDeliveryPreferences' },
    });
  });

  const isSubmittingOrder = useSelector(actorRef, (state) => {
    return (
      isPaymentPending ||
      state.matches({ ordering: { loading: 'confirmingStripe' } }) ||
      state.matches({ ordering: { loading: 'submittingOrder' } }) ||
      state.matches({ ordering: { idle: 'postOrder' } }) ||
      state.matches({ ordering: { loading: 'fetchingStatePostOrder' } })
    );
  });

  const isPostOrder = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'postOrder' } });
  });

  const isApplyingGiftCard = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'applyingGiftCard' },
    });
  });

  const isAddingUpsellToBag = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'addingUpsellToBag' },
    });
  });

  const isLineItemQuantityChangePending = useSelector(actorRef, (state) => {
    return state.context.lineItemIdsBeingUpdated.length > 0;
  });

  const isPerformingBagAction = useSelector(actorRef, (state) => {
    const { context: ctx } = state;
    const isRefetchingRewards =
      ctx.rewards.length > 0 &&
      (state.matches({ rewards: 'fetchingRewards' }) ||
        state.matches({ rewards: 'fetchingRewardsDeferred' }));

    return (
      Boolean(ctx.rewardIdBeingUpdated) ||
      isRefetchingRewards ||
      state.matches({ ordering: 'loading' })
    );
  });

  const isShowingFutureDateWarning = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'showingFutureDateWarning' },
    });
  });

  const isShowingRewardWarning = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'showingRewardWarning' },
    });
  });

  const isShowingWarning = useSelector(actorRef, (state) => {
    return (
      state.matches({
        ordering: { idle: 'showingRewardWarning' },
      }) ||
      state.matches({
        ordering: { idle: 'showingFutureDateWarning' },
      }) ||
      state.matches({
        ordering: { idle: 'timeslotAdjustedNotice' },
      })
    );
  });

  const isShowingCheckoutLedger = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'checkoutLedger' } });
  });

  const isShowingUpsellInterstitial = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'upsellInterstitial' } });
  });

  const isAddingUpsellFromInterstitial = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: { upsellInterstitial: 'addingUpsell' } } });
  });

  const areUpsellInteractionsDisabled = isPerformingBagAction || isLineItemQuantityChangePending;

  const isShowingOrderPlayback = useSelector(actorRef, (state) => {
    const { context } = state;

    const isNotPostRedirect = context.hasTriggeredOrderSubmission;
    const isOrderPlayback = state.matches({ ordering: { idle: 'orderPlayback' } });

    // Our loading state for order submission is the order playback view.
    const shouldShowOrderPlayback = isOrderPlayback || isSubmittingOrder;

    // After redirect we don't want to show order playback again.
    return isNotPostRedirect && shouldShowOrderPlayback;
  });

  const isShowingTimeslotAdjustedNotice = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'timeslotAdjustedNotice' },
    });
  });

  const isShowingNextDayOrderNotice = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'nextDayOrderNotice' },
    });
  });

  const isChangingPaymentMethod = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'changingPaymentMethod' },
    });
  });

  const isShowingGiftCardForm = useSelector(actorRef, (state) => {
    return (
      state.matches({
        ordering: { loading: 'applyingGiftCard' },
      }) || state.matches({ ordering: { idle: 'giftCardForm' } })
    );
  });

  const isShowingLoyaltyInfo = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'loyaltyInfo' } });
  });

  const isShowingLoyaltyTerms = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'loyaltyTerms' } });
  });

  const isShowingModal =
    isChangingTime ||
    isChangingDeliveryPreferences ||
    isShowingCheckoutLedger ||
    isChangingPaymentMethod ||
    isShowingGiftCardForm ||
    isShowingLoyaltyInfo ||
    isShowingLoyaltyTerms ||
    isApplyingGiftCard ||
    isShowingNextDayOrderNotice ||
    isShowingOrderPlayback ||
    isShowingUpsellInterstitial;

  const hasLineItems = (context?.cart?.lineItems?.length ?? 0) > 0;
  const isBagEmpty = !isFetchingInitialData && !hasLineItems && !isShowingModal;
  const isBagValid = !isFetchingInitialData && hasLineItems && !isPostOrder;

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const startOrdering = useCallback(() => {
    actorRef.send({ type: 'START' });
    trackBagView();
  }, [actorRef, trackBagView]);

  const startPostRedirectDelay = useCallback(() => {
    actorRef.send({ type: 'START_POST_REDIRECT_DELAY' });
  }, [actorRef]);

  const cancelRequest = useCallback(
    (overrideCheck = false) => {
      const shouldOverride = typeof overrideCheck === 'boolean' ? overrideCheck : false; // prettier-ignore
      // Prevents cancelling requests (closing modals) during order submission.
      // This check will be overwritten if the submission fails and needs to be canceled.
      if (isSubmittingOrder && !shouldOverride) return;

      actorRef.send({ type: 'CANCEL_REQUEST' });
    },
    [actorRef, isSubmittingOrder],
  );

  const flagFailureAsShown = useCallback(() => {
    actorRef.send({ type: 'SET_STRIPE_FAILURE_AS_ALREADY_SHOWN' });
  }, [actorRef]);

  const setConfirmStripe = useCallback(
    (confirmStripe: () => Promise<void>) => {
      actorRef.send({ type: 'SET_CONFIRM_STRIPE', params: { confirmStripe } });
    },
    [actorRef],
  );

  const requestChannelOrLocationChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_CHANNEL_LOCATION_CHANGE' });
    trackLocationNamePressed();
  }, [actorRef, trackLocationNamePressed]);

  const requestTimeChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_TIME_CHANGE' });
    trackWantedTimePressed();
  }, [actorRef, trackWantedTimePressed]);

  const changeTime = useCallback(
    (wantedTime: WantedTime) => {
      const validTime = adjustWantedTime(wantedTime);
      const isFutureDate = validTime ? DateFns.isTomorrow(validTime) : false;
      actorRef.send({
        type: 'CHANGE_TIME',
        params: { wantedTime, isFutureDate },
      });
      trackWantedTimeChanged();
    },
    [actorRef, trackWantedTimeChanged],
  );

  const startWantedTimesPolling = useCallback(() => {
    actorRef.send({ type: 'START_WANTED_TIMES_POLLING' });
  }, [actorRef]);

  const stopWantedTimesPolling = useCallback(() => {
    actorRef.send({ type: 'STOP_WANTED_TIMES_POLLING' });
  }, [actorRef]);

  const startBagCartPolling = useCallback(() => {
    actorRef.send({ type: 'START_CART_POLLING' });
  }, [actorRef]);

  const stopBagCartPolling = useCallback(() => {
    actorRef.send({ type: 'STOP_CART_POLLING' });
  }, [actorRef]);

  const changeTip = useCallback(
    (tip: number) => {
      actorRef.send({ type: 'CHANGE_TIP', params: { tip } });
    },
    [actorRef],
  );

  const requestDeliveryPreferencesChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_DELIVERY_PREFERENCES_CHANGE' });
    trackDeliveryPreferencesPressed();
  }, [actorRef, trackDeliveryPreferencesPressed]);

  const changeDeliveryPreferences = useCallback(
    (params: ChangeDeliveryPreferences) => {
      actorRef.send({ type: 'CHANGE_DELIVERY_PREFERENCES', params });
    },
    [actorRef],
  );

  const changeDropoffLocation = useCallback(
    (dropoffLocation: string) => {
      const params = { dropoffLocation };
      actorRef.send({ type: 'CHANGE_DROPOFF_LOCATION', params });
      trackDropoffLocationChanged();
    },
    [actorRef, trackDropoffLocationChanged],
  );

  const addUpsell = useCallback(
    (params: AddUpsellToBag) => {
      actorRef.send({ type: 'ADD_UPSELL_TO_BAG', params });
    },
    [actorRef],
  );

  const startChangingLineItemQuantity = useCallback(
    (lineItemId: string) => {
      actorRef.send({ type: 'START_CHANGING_LINE_ITEM_QUANTITY', params: { lineItemId } });
    },
    [actorRef],
  );

  const stopChangingLineItemQuantity = useCallback(
    (lineItemId: string) => {
      actorRef.send({ type: 'STOP_CHANGING_LINE_ITEM_QUANTITY', params: { lineItemId } });
    },
    [actorRef],
  );

  const changeLineItemQuantity = useCallback(
    (params: ChangeLineItemQuantity) => {
      actorRef.send({ type: 'CHANGE_LINE_ITEM_QUANTITY', params });

      if (params.quantity > 0) {
        trackLineItemQuantityChanged();
        return;
      }

      trackLineItemRemoved();
    },
    [actorRef, trackLineItemQuantityChanged, trackLineItemRemoved],
  );

  const changeUtensilsUsage = useCallback(
    (params: ChangeUtensilsUsage) => {
      actorRef.send({ type: 'CHANGE_UTENSILS_USAGE', params });
      trackUtensilsUsagePressed();
    },
    [actorRef, trackUtensilsUsagePressed],
  );

  const showLoyaltyInfo = useCallback(() => {
    actorRef.send({ type: 'SHOW_LOYALTY_INFO' });
    trackLoyaltyInfoPressed();
  }, [actorRef, trackLoyaltyInfoPressed]);

  const showLoyaltyTerms = useCallback(
    (rewardId: string) => {
      actorRef.send({ type: 'SHOW_LOYALTY_TERMS', params: { rewardId } });
      trackLoyaltyTermsPressed();
    },
    [actorRef, trackLoyaltyTermsPressed],
  );

  const applyBagReward = useCallback(
    (rewardId: string) => {
      const params = { rewardId, orderId: context.cart.orderId ?? '' };
      actorRef.send({ type: 'APPLY_REWARD', params });
      trackApplyRewardPressed();
    },
    [context.cart.orderId, actorRef, trackApplyRewardPressed],
  );

  const removeBagReward = useCallback(
    (rewardId: string) => {
      const params = { rewardId, orderId: context.cart.orderId ?? '' };
      actorRef.send({ type: 'REMOVE_REWARD', params });
      trackRemoveRewardPressed();
    },
    [context.cart.orderId, actorRef, trackRemoveRewardPressed],
  );

  const refetchRewards = useCallback(() => {
    actorRef.send({ type: 'REFETCH_REWARDS' });
  }, [actorRef]);

  const applyBagPromoCode = useCallback(
    (code: string) => {
      actorRef.send({ type: 'APPLY_PROMO_CODE', params: { code } });
      trackPromoCodeAddPressed();
    },
    [actorRef, trackPromoCodeAddPressed],
  );

  const requestCheckoutLedger = useCallback(() => {
    actorRef.send({ type: 'REQUEST_CHECKOUT_LEDGER' });
  }, [actorRef]);

  const requestOrderPlayback = useCallback(
    (paymentMethodId?: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'SHOW_ORDER_PLAYBACK', params });
    },
    [actorRef],
  );

  const requestPaymentMethodChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_PAYMENT_METHOD_CHANGE' });
    trackChangePaymentMethodPressed();
  }, [actorRef, trackChangePaymentMethodPressed]);

  const changePaymentMethod = useCallback(
    (paymentMethodId: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'CHANGE_PAYMENT_METHOD', params });
    },
    [actorRef],
  );

  const finishPaymentSelection = useCallback(() => {
    actorRef.send({ type: 'FINISH_PAYMENT_SELECTION' });
  }, [actorRef]);

  const changeCreditUsage = useCallback(
    (shouldUseCredit: boolean) => {
      const params = { shouldUseCredit };
      actorRef.send({ type: 'CHANGE_CREDIT_USAGE', params });
      trackCreditPressed();
    },
    [actorRef, trackCreditPressed],
  );

  const requestGiftCardForm = useCallback(() => {
    actorRef.send({ type: 'REQUEST_GIFT_CARD_FORM' });
    trackRedeemGiftCardPressed();
  }, [actorRef, trackRedeemGiftCardPressed]);

  const generateAwsWafTokenRef = useAwsWafTokenRef();

  const submitGiftCardForm = useCallback(
    async (code: string, regCode: string) => {
      const awsWafToken = await generateAwsWafTokenRef.current();
      const params = { code, regCode, awsWafToken };
      actorRef.send({ type: 'APPLY_GIFT_CARD', params });
      trackRedeemGiftCardSubmitted();
    },
    [actorRef, trackRedeemGiftCardSubmitted, generateAwsWafTokenRef],
  );

  // The stripe confirmation is triggered by the Stripe SDK.
  // It takes a few seconds so we need it to be in the machine to show loading states.
  const startStripeConfirmation = useCallback(() => {
    if (!isPaymentStatePollingEnabled) return;
    actorRef.send({ type: 'START_STRIPE_CONFIRMATION' });
    clearPaymentState();
  }, [isPaymentStatePollingEnabled, actorRef, clearPaymentState]);

  const startOrderPolling = useCallback(
    (paymentMethodId?: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'CHANGE_PAYMENT_METHOD', params });
      actorRef.send({ type: 'HAS_STARTED_POLLING' });
      startPolling(context.orderId ?? context.cart?.orderId ?? '');
    },
    [actorRef, context.orderId, context.cart?.orderId, startPolling],
  );

  const requestOrderSubmission = useCallback(
    (paymentMethodId?: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'SUBMIT_ORDER', params });
    },
    [actorRef],
  );

  const assignRewards = useCallback(
    (rewards: BagReward[]) => {
      const data = { rewards };
      actorRef.send({ type: 'ASSIGN_REWARDS', data });
    },
    [actorRef],
  );

  const onRewardsFetchFailure = useCallback(() => {
    actorRef.send({ type: 'REWARDS_FETCH_ERROR' });
  }, [actorRef]);

  // ─── Total ─────────────────────────────────────────────────────────────────

  const { total, creditToBeApplied } = useMemo(
    () =>
      sumLedgerTotal(
        context.cart.ledger,
        context.selectedTip,
        context.availableCredit,
        context.shouldUseCredit,
      ),
    [context.cart.ledger, context.selectedTip, context.availableCredit, context.shouldUseCredit],
  );

  // ─── Upsell Items Filtering ────────────────────────────────────────────────────

  const upsellItems = useMemo(() => {
    return getMaxAvailableUpsellItems({
      lineItems: context.cart.lineItems,
      upsellItems: context.upsellItems,
      maxNumberOfUpsellProducts: context.maxNumberOfUpsellProducts,
    });
  }, [context.cart.lineItems, context.maxNumberOfUpsellProducts, context.upsellItems]);

  // ─── Deferred helpers ────────────────────────────────────────────────

  useBagLoyaltyRewardsDeferred({
    shouldFetchRewards: isFetchingRewardsDeferred,
    onSuccess: assignRewards,
    onFailure: onRewardsFetchFailure,
  });

  // ─── Back Behavior ───────────────────────────────────────────────────

  const canGoBack = isChangingPaymentMethod || isShowingGiftCardForm;

  const backBehavior = useCallback(() => {
    if (isChangingPaymentMethod) {
      actorRef.send({ type: 'REQUEST_CHECKOUT_LEDGER' });
    }

    if (isShowingGiftCardForm) {
      requestPaymentMethodChange();
    }
  }, [actorRef, isChangingPaymentMethod, isShowingGiftCardForm, requestPaymentMethodChange]);

  const goBack = canGoBack ? backBehavior : undefined;

  // ─── Return ────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      ...context,

      upsellItems,

      bagTracking,
      total,
      creditToBeApplied,
      registerStripeConfirmHandler,
      goBack,

      isFetchingInitialData,
      isFetchingRewards,
      hasFailedToLoadRewards,
      isChangingLineItemQuantity,
      isApplyingPromoCode,
      isChangingTime,
      isChangingDeliveryPreferences,
      isCancelingPayment,
      isSubmittingOrder,
      isPostOrder,
      isPollingPayment,
      isPaymentPending,
      isPaymentDelayed,
      isPaymentFulfilled,
      isApplyingGiftCard,
      isAddingUpsellToBag,
      isLineItemQuantityChangePending,
      isPerformingBagAction,
      isShowingFutureDateWarning,
      isShowingRewardWarning,
      isShowingWarning,
      isShowingCheckoutLedger,
      isShowingUpsellInterstitial,
      isAddingUpsellFromInterstitial,
      areUpsellInteractionsDisabled,
      isShowingOrderPlayback,
      isShowingTimeslotAdjustedNotice,
      isShowingNextDayOrderNotice,
      isChangingPaymentMethod,
      isShowingGiftCardForm,
      isShowingLoyaltyInfo,
      isShowingLoyaltyTerms,
      isShowingModal,
      isBagEmpty,
      isBagValid,

      startOrdering,
      startPostRedirectDelay,
      cancelRequest,
      cancelPreviousIntent: requestPaymentCancelation,
      createPayment,
      failPayment,
      flagFailureAsShown,
      setConfirmStripe,
      requestChannelOrLocationChange,
      requestTimeChange,
      changeTime,
      startWantedTimesPolling,
      stopWantedTimesPolling,
      startBagCartPolling,
      stopBagCartPolling,
      changeTip,
      requestDeliveryPreferencesChange,
      changeDeliveryPreferences,
      changeDropoffLocation,
      addUpsell,
      startChangingLineItemQuantity,
      stopChangingLineItemQuantity,
      changeLineItemQuantity,
      changeUtensilsUsage,
      showLoyaltyInfo,
      showLoyaltyTerms,
      applyBagReward,
      removeBagReward,
      refetchRewards,
      applyBagPromoCode,
      requestCheckoutLedger,
      requestOrderPlayback,
      requestPaymentMethodChange,
      changePaymentMethod,
      finishPaymentSelection,
      changeCreditUsage,
      requestGiftCardForm,
      generateAwsWafTokenRef,
      submitGiftCardForm,
      startStripeConfirmation,
      startOrderPolling,
      requestOrderSubmission,
    }),
    [
      addUpsell,
      applyBagPromoCode,
      applyBagReward,
      bagTracking,
      requestPaymentCancelation,
      createPayment,
      failPayment,
      cancelRequest,
      changeCreditUsage,
      changeDeliveryPreferences,
      changeDropoffLocation,
      changeLineItemQuantity,
      changePaymentMethod,
      changeTime,
      changeTip,
      changeUtensilsUsage,
      context,
      creditToBeApplied,
      finishPaymentSelection,
      flagFailureAsShown,
      goBack,
      hasFailedToLoadRewards,
      isAddingUpsellToBag,
      isApplyingGiftCard,
      isApplyingPromoCode,
      isBagEmpty,
      isBagValid,
      isCancelingPayment,
      isChangingDeliveryPreferences,
      isChangingLineItemQuantity,
      isChangingPaymentMethod,
      isChangingTime,
      isFetchingInitialData,
      isFetchingRewards,
      isLineItemQuantityChangePending,
      isPerformingBagAction,
      isPostOrder,
      isPollingPayment,
      isPaymentPending,
      isPaymentDelayed,
      isPaymentFulfilled,
      isShowingCheckoutLedger,
      isShowingUpsellInterstitial,
      isAddingUpsellFromInterstitial,
      areUpsellInteractionsDisabled,
      isShowingFutureDateWarning,
      isShowingGiftCardForm,
      isShowingLoyaltyInfo,
      isShowingLoyaltyTerms,
      isShowingModal,
      isShowingNextDayOrderNotice,
      isShowingOrderPlayback,
      isShowingRewardWarning,
      isShowingTimeslotAdjustedNotice,
      isShowingWarning,
      isSubmittingOrder,
      upsellItems,
      refetchRewards,
      startWantedTimesPolling,
      stopWantedTimesPolling,
      startBagCartPolling,
      stopBagCartPolling,
      registerStripeConfirmHandler,
      removeBagReward,
      requestChannelOrLocationChange,
      requestCheckoutLedger,
      requestDeliveryPreferencesChange,
      requestGiftCardForm,
      generateAwsWafTokenRef,
      requestOrderPlayback,
      requestOrderSubmission,
      requestPaymentMethodChange,
      requestTimeChange,
      setConfirmStripe,
      showLoyaltyInfo,
      showLoyaltyTerms,
      startChangingLineItemQuantity,
      startOrderPolling,
      startOrdering,
      startPostRedirectDelay,
      startStripeConfirmation,
      stopChangingLineItemQuantity,
      submitGiftCardForm,
      total,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type BagTracking = {
  trackBagView: () => void;
  trackLocationNamePressed: () => void;
  trackWantedTimePressed: () => void;
  trackWantedTimeChanged: () => void;
  trackDeliveryPreferencesPressed: () => void;
  trackDropoffLocationChanged: () => void;
  trackViewMenuPressed: () => void;
  trackLineItemPressed: () => void;
  trackLineItemRemoved: () => void;
  trackLineItemQuantityChanged: () => void;
  trackLoyaltyInfoPressed: () => void;
  trackLoyaltyTermsPressed: () => void;
  trackLoyaltySignedOutPressed: () => void;
  trackApplyRewardPressed: () => void;
  trackRemoveRewardPressed: () => void;
  trackPromoCodeAddPressed: () => void;
  trackUtensilsUsagePressed: () => void;
  trackContinuePressed: () => void;
  trackContinueDeliveryMinimumBlocked: (
    minimum: string | number,
    amountNeeded: string | number,
  ) => void;
  trackChangePaymentMethodPressed: () => void;
  trackCreditPressed: () => void;
  trackPaymentMethodPressed: () => void;
  trackRedeemGiftCardPressed: () => void;
  trackRedeemGiftCardSubmitted: () => void;
  trackUpsellExperimentStarted: (placementVariation: string) => void;
  trackUpsellModalContinuePressed: () => void;
  trackUpsellModalClosePressed: () => void;
};

type UseOrderingParams = {
  isLoggedIn: boolean;
  orderSubmissionDelay: number;
  bagCartPollingInterval: number;
  isPaymentStatePollingEnabled: boolean;
  bagTracking: BagTracking;
  handlePostOrderPurchaseModal: OrderingMachineAction;
  handlePostOrderNotifications: OrderingMachineAction;
  handlePostOrderTracking: OrderingMachineAction;
  handlePostOrderHistory: OrderingMachineAction;
  handlePostOrderAppReview: OrderingMachineAction;
  navigatePostOrder: OrderingMachineAction;
  changeChannelOrLocation: OrderingMachineAction;
  requestLogin: OrderingMachineAction;

  fetchInitialData: typeof OrderingActors.fetchInitialData;
  fetchStatePostOrder: typeof OrderingActors.fetchStatePostOrder;
  fetchRewards: typeof OrderingActors.fetchRewards;
  fetchWantedTimes: typeof OrderingActors.fetchWantedTimes;
  fetchBagCartPolling: typeof OrderingActors.fetchBagCartPolling;
  addUpsellToBag: typeof OrderingActors.addUpsellToBag;
  updateLineItem: typeof OrderingActors.updateLineItem;
  applyReward: typeof OrderingActors.applyReward;
  removeReward: typeof OrderingActors.removeReward;
  applyPromoCode: typeof OrderingActors.applyPromoCode;
  applyGiftCard: typeof OrderingActors.applyGiftCard;
  submitOrder: typeof OrderingActors.submitOrder;

  fetchOrderPaymentState: typeof PaymentStatePollingActors.fetchPaymentState;
  cancelPreviousIntent: typeof PaymentStatePollingActors.cancelPayment;
};
